import { isUndefined } from '@aloft/toolkit-shared';

import { useState } from 'react';

import { useTkv2NullableOrderContext } from '../../controller/hooks/useTkv2NullableControllerOrder.js';
import { useTkv2OrderID } from '../../controller/hooks/useTkv2OrderID.js';
import { useGetVacantLandSales } from '../../hooks/api/schema-hooks/useGetVacantLandSales.js';
import { usePropertyCoordinates } from '../../hooks/usePropertyCoordinates.js';
import { formatReferenceViewID } from '../../modules/referenceView/referenceViewID/formatReferenceViewID.js';
import { selectCraftsmanMetadataById } from '../../redux/features/selectors/selectCraftsmanMetadataById.js';
import { useAppSelector } from '../../redux/hooks.js';
import { Typography } from '../../summary/components/Typography.js';
import { VacantLandSalesMap } from './VacantLandSalesMap.js';
import { VacantLandSalesTable } from './VacantLandSalesTable.js';
import { constellation1ListingsToVacantLandSalesProps } from './constellation1ListingsToVacantLandSalesProps.js';

import './VacantLandSales.scss';

export const VacantLandSales = ({ isPopout }: { isPopout: boolean }) => {
  const { subject } = useTkv2NullableOrderContext() ?? {};
  const subjectMetadata = useAppSelector(selectCraftsmanMetadataById)[
    formatReferenceViewID({ type: 'subject' })
  ];
  const orderID = useTkv2OrderID();
  const vacantLandSales = useGetVacantLandSales({ pathParams: { orderID: orderID ?? '' } }).data
    ?.body;
  const { isLoading: coordinatesAreLoading, coordinates } = usePropertyCoordinates({
    address: subject?.body.getStructuredData('address') ?? subjectMetadata?.address,
    listingID: subject?.body.getStructuredData('toolkitListingID') ?? subjectMetadata?.listingID,
  });

  const [focusedRow, setFocusedRow] = useState<string | undefined>(undefined);

  if (vacantLandSales?.body.status !== 'found') return null;
  if (coordinatesAreLoading || isUndefined(coordinates)) return null;

  const vacantLandSalesProperties = constellation1ListingsToVacantLandSalesProps(
    { lat: coordinates.lat, lng: coordinates.lng },
    vacantLandSales?.body.output.results,
  );

  const focusedPropertyIndex = vacantLandSalesProperties.findIndex(
    (property) => property.id === focusedRow,
  );
  const focusedPropertyId =
    focusedPropertyIndex !== -1 ? vacantLandSalesProperties[focusedPropertyIndex]?.id : undefined;
  const setFocusedPropertyId = (id: string | undefined) => {
    setFocusedRow(id);
  };

  return (
    <div className="vacant-land-sales-container">
      <Typography variant="h3">Vacant Land Sales</Typography>
      <div className={`vacant-land-sales-data${isPopout ? ' vacant-land-sales-popout' : ''}`}>
        <VacantLandSalesTable
          vacantLandSales={vacantLandSalesProperties}
          orderID={orderID}
          onRowClick={(row) => setFocusedRow(row.id)}
          focusedRowIndex={focusedPropertyIndex}
        />
        <VacantLandSalesMap
          vacantLandSales={vacantLandSalesProperties}
          focusedPropertyId={focusedPropertyId}
          setFocusedPropertyId={setFocusedPropertyId}
          subjectLat={coordinates.lat}
          subjectLng={coordinates.lng}
        />
      </div>
    </div>
  );
};
